<!--
LoadingButton should be used when a button is clicked and the user should be informed that the action is in progress.

There are two ways to use LoadingButton:

-> Through `callback` - Component consumer sends a callback and spinner is shown while the callback is running
-> Through `showSpinner` - Component consumer controls directly the spinner state through the prop
-->

<template>
  <!-- Button is always disabled if spinner is showing -->
  <button
    v-bind="filteredAttrs"
    class="flex items-center"
    :disabled="actionOngoing || disabled"
    @click="onClick"
  >
    <InButtonSpinner
      v-show="actionOngoing"
      data-testid="button-spinner"
      :class="`${spinnerColourClass} -ml-1 mr-3 h-5 w-5`"
    />
    <slot></slot>
  </button>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, useAttrs } from 'vue'
import InButtonSpinner from './InButtonSpinner.vue'

interface Args {
  callback?: () => Promise<void>
  showSpinner?: boolean
}

export default defineComponent({
  components: {
    InButtonSpinner,
  },
  inheritAttrs: false,
  props: {
    // Must be a declared prop (not a fallthrough attr) so disabled updates
    // reactively when the parent binding changes.
    disabled: {
      type: Boolean,
      default: false,
    },
    args: {
      type: Object as PropType<Args>,
      required: true,
      validator(value: Args) {
        return (
          (value.showSpinner !== undefined && value.callback === undefined) ||
          (value.showSpinner === undefined && value.callback !== undefined)
        )
      },
    },
    spinnerColourClass: {
      type: String,
      default: 'text-white',
    },
  },
  data() {
    return {
      submitting: false,
    }
  },
  computed: {
    actionOngoing(): boolean {
      return this.submitting || !!this.args.showSpinner
    },
    filteredAttrs() {
      return useAttrs()
    },
  },
  methods: {
    async onClick(): Promise<void> {
      if (!this.submitting && this.args.callback) {
        try {
          this.submitting = true
          await this.args.callback()
        } finally {
          this.submitting = false
        }
      }
    },
  },
})
</script>
